import { useState, useEffect, useMemo } from "react";
import {
  Table,
  Select,
  Button,
  Modal,
  Form,
  Dropdown,
  Menu,
  Spin,
  Input,
  message,
} from "antd";
import {
  useAddressById,
  useAddressList,
  useCreateAddress,
  useDeleteAddress,
  useUpdateAddress,
} from "../api/address";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";

const AddressPage: React.FC = () => {
  const { data: regionData, isLoading: regionsLoading } = useAddressList();
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [selectedWoreda, setSelectedWoreda] = useState<string | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [woredas, setWoredas] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [regionZones, setRegionZones] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [allRegions, setAllRegions] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const { data: addressData, refetch } = useAddressById(selectedRegion ?? "");
  const { mutateAsync: createAddress } = useCreateAddress();
  const { mutateAsync: updateAddress } = useUpdateAddress();
  const { mutateAsync: deleteAddress } = useDeleteAddress();

  useEffect(() => {
    if (regionData?.data) setAllRegions(regionData.data);
  }, [regionData]);

  useEffect(() => {
    if (selectedRegion) {
      const fetchRegionData = async () => {
        setLoading(true);
        const { data } = await refetch();
        const region = data?.data?.data?.content?.find(
          (r: any) => r.regionId === Number(selectedRegion)
        );

        if (region) {
          const zones =
            region.zoneDtos?.map((zone: any) => ({
              ...zone,
              regionName: region.regionName,
            })) || [];

          setRegionZones(zones);
          setZones(zones);
          setWoredas([]); // Reset woredas when region changes
          setFilteredData(zones);
        }
        setLoading(false);
      };
      fetchRegionData();
    } else {
      setRegionZones([]);
      setZones([]);
      setWoredas([]);
      setFilteredData([]);
    }
  }, [selectedRegion, refetch]);

  useEffect(() => {
    if (selectedZone) {
      const selectedZoneData = regionZones.find(
        (z) => z.zoneId === Number(selectedZone)
      );

      if (selectedZoneData) {
        const filteredWoredas =
          selectedZoneData.woredaDtos?.map((w: any) => ({
            ...w,
            zoneName: selectedZoneData.zoneName,
            regionName: selectedZoneData.regionName,
          })) || [];

        setWoredas(filteredWoredas);

        setFilteredData([
          {
            ...selectedZoneData,
            woredaDtos: filteredWoredas.length > 0 ? filteredWoredas : [],
          },
        ]);
      }
    } else if (selectedRegion) {
      setWoredas([]);
      setFilteredData(regionZones);
    }
  }, [selectedZone, regionZones, selectedRegion]);

  useEffect(() => {
    if (selectedWoreda) {
      const filteredZonesWithWoredas = regionZones
        .map((zone) => ({
          ...zone,
          woredaDtos:
            zone.woredaDtos?.filter(
              (w: any) => w.woredaId === Number(selectedWoreda)
            ) || [],
        }))
        .filter((zone) => zone.woredaDtos.length > 0);

      setFilteredData(filteredZonesWithWoredas);
    }
  }, [selectedWoreda, regionZones]);

  const handleRegionChange = async (regionId: string | null) => {
    setSelectedRegion(regionId);
    setSelectedZone(null);
    setSelectedWoreda(null);
    setZones([]);
    setWoredas([]);
    setFilteredData([]);

    if (regionId) {
      setLoading(true);
      try {
        const { data } = await refetch();
        const zoneList =
          data?.data?.map((zone: any) => ({
            ...zone,
            regionName:
              allRegions.find((r: any) => r.id === Number(regionId))?.name ||
              "",
          })) || [];
        setZones(zoneList);
        setFilteredData(zoneList);
      } catch (err) {
        console.error("Error fetching address data:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleZoneChange = (zoneId: string | null) => {
    setSelectedZone(zoneId);
    setSelectedWoreda(null);
  };

  const handleWoredaChange = (woredaId: string | null) =>
    setSelectedWoreda(woredaId);
  const handleClearFilters = () => {
    setSelectedRegion(null);
    setSelectedZone(null);
    setSelectedWoreda(null);
    setZones([]);
    setWoredas([]);
    setFilteredData([]);
  };

  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [dropdownOpen, setDropdownOpen] = useState({
    region: false,
    zone: false,
    woreda: false,
  });

  const handleAdd = () => setIsAddModalVisible(true);
  const handleAddCancel = () => setIsAddModalVisible(false);

  const handleAddSubmit = async () => {
    setIsSaving(true); // start loading
    try {
      const values = await form.validateFields();

      const payload = {
        regionName: values.regionName?.trim() || "",
        regionCode: values.regionCode?.trim() || "",
        zoneName: values.zoneName?.trim() || "",
        zoneCode: values.zoneCode?.trim() || "",
        woredaName: values.woredaName?.trim() || "",
        woredaCode: values.woredaCode?.trim() || "",
      };

      if (!payload.regionName || !payload.regionCode) {
        message.error("Region Name and Region Code are required.");
        return;
      }

      const result = await createAddress(payload);

      const newAddressId = result?.data?.data?.id || result?.data?.id;

      if (newAddressId) {
        message.success("Address created successfully!");
        setIsAddModalVisible(false);
        form.resetFields();
      } else {
        console.error("Unexpected response structure:", result);
        message.error("Something went wrong while creating the address.");
      }
    } catch (error: any) {
      console.error("Error in handleAddSubmit:", error);
      message.error("Submission failed. Please check your input.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async (record: any) => {
    setEditing(record);
    setIsEditMode(true);
    setIsAddModalVisible(true);
    try {
      const payload = form.setFieldsValue({
        regionName: record.regionName,
        regionCode: record.regionCode,
        zoneName: record.zoneName,
        zoneCode: record.zoneCode,
        woredaName: record?.woredaDtos?.[0]?.woredaName || "",
        woredaCode: record?.woredaDtos?.[0]?.woredaCode || "",
      });

      const result = await updateAddress(payload);
      if (result) {
        message.success("Address updated successfully!");
        setIsAddModalVisible(false);
        form.resetFields();
      } else {
        console.error("Unexpected response structure:", result);
        message.error("Something went wrong while updating the address.");
      }
    } catch (error: any) {
      console.error("Error in handleEdit:", error);
      message.error("Edit failed. Please check your input.");
    } finally {
      setIsSaving(false);
      setIsAddModalVisible(false);
      form.resetFields();
    }
  };

  const handleDelete = async (record: any) => {
    if (!record.woredaId) {
      message.error("Woreda Id is required!");
    }

    try {
      await deleteAddress(record.woredaId);
      message.success("Deleted successfully");
      refetch();
    } catch (err) {
      console.error("Delete error", err);
      message.error("Delete failed");
    }
  };

  const columns = useMemo(
    () => [
      { title: "Region", dataIndex: "regionName", key: "regionName" },
      { title: "Zone", dataIndex: "zoneName", key: "zoneName" },
      {
        title: "Woredas",
        dataIndex: "woredaDtos",
        key: "woredaDtos",
        render: (woredaDtos: any) =>
          woredaDtos && woredaDtos.length > 0 ? (
            <ul>
              {woredaDtos.map((w: any) => (
                <li key={w.woredaId}>{w.woredaName}</li>
              ))}
            </ul>
          ) : (
            "-"
          ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_: any, record: any) => (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="edit" onClick={() => handleEdit(record)}>
                  <span className="text-gray-500">Edit</span>
                </Menu.Item>
                <Menu.Item key="delete" onClick={() => handleDelete(record)}>
                  <span className="text-gray-500">Delete</span>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        ),
      },
    ],
    []
  );

  return (
    <div className="p-4 bg-white rounded-lg shadow-md h-full mt-4">
      <div className="flex gap-4 mb-4">
        <Select
          allowClear
          placeholder="--- Select Region ---"
          className="w-1/4"
          loading={regionsLoading}
          onChange={handleRegionChange}
          value={selectedRegion}
        >
          {regionData?.data?.map((region: any) => (
            <Select.Option key={region.id} value={region.id}>
              {region.name}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="--- Select Zone ---"
          className="w-1/4"
          onChange={handleZoneChange}
          value={selectedZone}
          disabled={!selectedRegion}
        >
          {zones.map((zone: any) => (
            <Select.Option key={zone.zoneId} value={zone.zoneId}>
              {zone.zoneName}
            </Select.Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="--- Select Woreda ---"
          className="w-1/4"
          onChange={handleWoredaChange}
          value={selectedWoreda}
          disabled={!selectedZone}
        >
          {woredas.map((woreda: any) => (
            <Select.Option key={woreda.woredaId} value={woreda.woredaId}>
              {woreda.woredaName}
            </Select.Option>
          ))}
        </Select>

        <Button onClick={handleClearFilters}>Clear Filters</Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={handleAdd}
          loading={isSaving}
        >
          Add
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-6">
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={filteredData}
          columns={columns}
          locale={{ emptyText: "No Data to Display" }}
          rowKey={(record: any) =>
            record.id || record.zoneId || record.woredaId
          }
        />
      )}
      <Modal
        title="Add Address"
        open={isAddModalVisible}
        onCancel={handleAddCancel}
        onOk={handleAddSubmit}
        okText="Save"
        confirmLoading={isSaving}
      >
        <Form className="mt-4" layout="vertical" form={form}>
          {/* REGION */}
          <div className="flex gap-2 w-full">
            <Form.Item className="w-1/2" label="Region Name" name="regionName">
              <Select
                showSearch={false}
                allowClear
                open={dropdownOpen.region}
                onDropdownVisibleChange={(open) =>
                  setDropdownOpen((prev) => ({ ...prev, region: open }))
                }
                dropdownRender={(menu) => (
                  <>
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Enter new or select region"
                        value={form.getFieldValue("regionName")}
                        onChange={(e) => {
                          const value = e.target.value;
                          form.setFieldValue("regionName", value);
                          form.setFieldValue("regionCode", "");
                          setZones([]);
                          setWoredas([]);
                          form.setFieldValue("zoneName", null);
                          form.setFieldValue("zoneCode", "");
                          form.setFieldValue("woredaName", null);
                          form.setFieldValue("woredaCode", "");
                        }}
                      />
                    </div>
                    {menu}
                  </>
                )}
                onSelect={(value) => {
                  form.setFieldValue("regionName", value);
                  const selectedRegion = allRegions.find(
                    (r) => r.name === value || r.id === value
                  );
                  form.setFieldValue("regionCode", selectedRegion?.code || "");
                  const relatedZones =
                    regionZones?.filter(
                      (z) => z.regionId === selectedRegion?.id
                    ) || [];
                  setZones(relatedZones);
                  setWoredas([]);
                  form.setFieldValue("zoneName", null);
                  form.setFieldValue("zoneCode", "");
                  form.setFieldValue("woredaName", null);
                  form.setFieldValue("woredaCode", "");
                }}
              >
                {allRegions.map((region) => (
                  <Select.Option key={region.id} value={region.name}>
                    {region.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item className="w-1/2" label="Region Code" name="regionCode">
              <Input placeholder="Region Code" />
            </Form.Item>
          </div>

          {/* ZONE */}
          <div className="flex gap-2 w-full">
            <Form.Item className="w-1/2" label="Zone Name" name="zoneName">
              <Select
                showSearch={false}
                allowClear
                open={dropdownOpen.zone}
                disabled={!form.getFieldValue("regionName")}
                onDropdownVisibleChange={(open) =>
                  setDropdownOpen((prev) => ({ ...prev, zone: open }))
                }
                dropdownRender={(menu) => (
                  <>
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Enter new or select zone"
                        value={form.getFieldValue("zoneName")}
                        onChange={(e) => {
                          const value = e.target.value;
                          form.setFieldValue("zoneName", value);
                          form.setFieldValue("zoneCode", "");
                          setWoredas([]);
                          form.setFieldValue("woredaName", null);
                          form.setFieldValue("woredaCode", "");
                        }}
                      />
                    </div>
                    {menu}
                  </>
                )}
                onSelect={(value) => {
                  form.setFieldValue("zoneName", value);
                  const selectedZone = zones.find(
                    (z) => z.zoneName === value || z.zoneId === value
                  );
                  form.setFieldValue("zoneCode", selectedZone?.zoneCode || "");
                  setWoredas(selectedZone?.woredaDtos || []);
                  form.setFieldValue("woredaName", null);
                  form.setFieldValue("woredaCode", "");
                }}
              >
                {zones.map((zone) => (
                  <Select.Option key={zone.zoneId} value={zone.zoneName}>
                    {zone.zoneName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item className="w-1/2" label="Zone Code" name="zoneCode">
              <Input placeholder="Zone Code" />
            </Form.Item>
          </div>

          {/* WOREDAS */}
          <div className="flex gap-2 w-full">
            <Form.Item className="w-1/2" label="Woreda Name" name="woredaName">
              <Select
                showSearch={false}
                allowClear
                open={dropdownOpen.woreda}
                disabled={!form.getFieldValue("zoneName")}
                onDropdownVisibleChange={(open) =>
                  setDropdownOpen((prev) => ({ ...prev, woreda: open }))
                }
                dropdownRender={(menu) => (
                  <>
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Enter new or select woreda"
                        value={form.getFieldValue("woredaName")}
                        onChange={(e) => {
                          const value = e.target.value;
                          form.setFieldValue("woredaName", value);
                          form.setFieldValue("woredaCode", "");
                        }}
                      />
                    </div>
                    {menu}
                  </>
                )}
                onSelect={(value) => {
                  form.setFieldValue("woredaName", value);
                  const selectedWoreda = woredas.find(
                    (w) => w.woredaName === value || w.woredaId === value
                  );
                  form.setFieldValue(
                    "woredaCode",
                    selectedWoreda?.woredaCode || ""
                  );
                }}
              >
                {woredas.map((woreda) => (
                  <Select.Option
                    key={woreda.woredaId}
                    value={woreda.woredaName}
                  >
                    {woreda.woredaName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item className="w-1/2" label="Woreda Code" name="woredaCode">
              <Input placeholder="Woreda Code" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddressPage;
